import { CircularProgress, InputLabel, MenuItem, Select } from '@material-ui/core';
import moment from 'moment';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { orderBy } from 'lodash';
import { ExpandMoreRounded } from '@material-ui/icons';

// Components.
import { EditOutcomeForm } from 'components/forms';

// Core.
import { TAXONOMY_ACTIVITY_REVIEW_OUTCOMES_ID, TAXONOMY_ACTIVITY_REVIEW_TYPES_ID } from 'core/constants';
import { IActivityReviewHistory } from 'core/models';

// Store.
import { getActivityReviewHistory } from 'store/activity/actions';
import { activityReviewHistorySelector } from 'store/activity/selectors';
import { getTaxonomyTermById } from 'store/taxonomy/actions';
import { activityReviewTypesSelector } from 'store/taxonomy/selectors';

// Styles.
import styles from './ActivityReviewOutcomes.module.scss';

interface IActivityReviewOutcomesProps {
  id: string;
  onClose?: VoidFunction;
  onSave?: VoidFunction;
}

export const ActivityReviewOutcomes = (props: IActivityReviewOutcomesProps): ReactElement => {
  const { id, onClose, onSave } = props;
  const dispatch = useDispatch();
  const { isLoadingReviewHistory, reviewHistory } = useSelector(activityReviewHistorySelector);
  const reviewTypes = useSelector(activityReviewTypesSelector);
  const eligibleEntries = useMemo(
    () => orderBy(reviewHistory?.filter((review) => review.canHaveOutcome) ?? [], ['reviewDate'], ['asc']),
    [reviewHistory],
  );
  const [selectedReview, setSelectedReview] = useState<IActivityReviewHistory | null>(null);
  useEffect(() => {
    setSelectedReview(eligibleEntries[0]);
  }, [eligibleEntries]);

  useEffect(() => {
    dispatch(getTaxonomyTermById(TAXONOMY_ACTIVITY_REVIEW_TYPES_ID));
    dispatch(getTaxonomyTermById(TAXONOMY_ACTIVITY_REVIEW_OUTCOMES_ID));
    dispatch(getActivityReviewHistory(id));
  }, [dispatch, id]);

  if (isLoadingReviewHistory)
    return (
      <div className={styles['loading-wrapper']}>
        <CircularProgress color="inherit" />
      </div>
    );
  if (eligibleEntries?.length === 0) {
    return (
      <section className={styles.section}>
        <p>This activity has no ongoing reviews</p>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <h2 className={styles.title}>
        <InputLabel>
          Review <span className={styles.required}>*</span>
        </InputLabel>
        <Select
          className={styles.select}
          defaultValue={eligibleEntries[0]?.id}
          onChange={(e) => {
            setSelectedReview(eligibleEntries.find(({ id }) => id === e.target.value));
          }}
          variant="outlined"
          IconComponent={ExpandMoreRounded}
        >
          {eligibleEntries.map(({ id, reviewTypeId, reviewDate }) => {
            const reviewType = reviewTypes?.find((item) => item.id === reviewTypeId);
            const formatedReviewDate = moment(reviewDate).format('MM-DD-YYYY');
            return (
              <MenuItem key={id} value={id}>
                {reviewType?.name} Review {formatedReviewDate}
              </MenuItem>
            );
          })}
        </Select>
      </h2>
      {selectedReview && (
        <EditOutcomeForm key={selectedReview?.id} entry={selectedReview} id={id} onClose={onClose} onSuccess={onSave} />
      )}
    </section>
  );
};
